import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
/** Components */
import { FormSuccess } from 'Components/Form/Success';
import { Main as Container } from 'Components/Main';
import { Detalhes } from './Details';
import { Vinculos } from './Bonds';
/** Services */
import { addSystem, editSystem, getSystemById } from 'services/systems-service';
import { getUsersName } from 'services/users-service';
import { getAreasShort } from 'services/areas-service';
import { getProcessesShort } from 'services/processes-service';
import { getAssetsShort } from 'services/assets-service';
/** State Managers */
import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';
import { useQuery, useQueries } from 'react-query';
import { PageLoaderForm } from 'Components/PageLoader/Form';
import { Dialog, PageHeader } from 'Components/ui';

const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';

const CancelBtn = styled.button`
  height: 44px;
  background: white;
  color: ${BLUE};
  border: 1.5px solid ${BLUE};
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SaveBtn = styled.button`
  height: 44px;
  background: ${BLUE};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: var(--vo-cobalto); }
  &:disabled { opacity: 0.7; cursor: not-allowed; }
`;

const TabBar = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  background: white;
  border-radius: 12px;
  padding: 5px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  width: fit-content;
`;

const TabBtn = styled.button`
  height: 36px;
  padding: 0 18px;
  border: none;
  border-radius: 9px;
  background: ${p => p.active ? BLUE : 'transparent'};
  color: ${p => p.active ? 'white' : MUTED};
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: ${p => p.active ? 600 : 500};
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const SystemsForm = () => {
  const { t } = useTranslation(['mapping', 'common']);
  const selectedTenant = useSelector(useAuthTenant);
  const { id } = useParams();
  const navigate = useNavigate();
  const [idSystem, setIdSystem] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [finished, setFinished] = useState(false);
  const [isLoadingForm, setIsLoadingForm] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      name: '',
      criticity: '',
      user: '',
      tags: [],
      pii: false,
      saas: false,
      developedInHouse: false,
      dataBase: '',
      description: '',
      areas: [],
      processes: [],
      assets: [],
      companyKey: null,
    },
  });

  const formatArea = area => ({ _id: area._id, name: area.name, description: area.description, user: area.user, criticity: area.criticity });
  const formatAsset = asset => ({ _id: asset._id, name: asset.name, description: asset.description, user: asset.user, criticity: asset.criticity });
  const formatProcess = process => ({ _id: process._id, name: process.name, description: process.description, user: process.user, criticity: process.criticity });

  const formatForm = dados => ({
    name: dados.name,
    criticity: dados.criticity,
    user: dados.user,
    tags: dados.tags.length > 0 ? dados.tags : [],
    pii: dados.pii ?? false,
    saas: dados.saas ?? false,
    developedInHouse: dados.developedInHouse ?? false,
    dataBase: dados.dataBase ?? '',
    description: dados.description ?? '',
    areas: dados.areas.length > 0 ? dados.areas.map(formatArea) : [],
    processes: dados.processes.length > 0 ? dados.processes.map(formatProcess) : [],
    assets: dados.assets.length > 0 ? dados.assets.map(formatAsset) : [],
    companyKey: dados.companyKey ?? selectedTenant.slug,
  });

  const handleComplete = async (formData) => {
    try {
      setIsLoadingForm(true);
      const formattedForm = formatForm(formData);
      const res = id ? await editSystem(id, formattedForm) : await addSystem(formattedForm);
      if (res.status === 200 || res.status === 201) {
        setIdSystem(res.data._id ?? id);
        setFinished(true);
        setIsLoadingForm(false);
      } else {
        throw new Error(t('mapping:systems.form.errorSave'));
      }
    } catch (error) {
      setIsLoadingForm(false);
      Dialog.alert({
        title: t('mapping:systems.toasts.errorTitle'),
        message: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const handleReset = () => {
    reset();
    setActiveTab(0);
    setFinished(false);
    setIdSystem(null);
    navigate('/systems/add');
  };

  const { isLoading, error, data: system } = useQuery(['getSystem', id], async () => {
    if (!id) return null;
    const resSystem = await getSystemById(id);
    if (!resSystem.data) return null;
    return resSystem.data;
  });

  useEffect(() => {
    if (!id || !system) return;
    Object.keys(system).forEach(key => setValue(key, system[key]));
  }, [id, system, setValue]);

  const { data: users } = useQuery(['getUsers'], async () => {
    const result = await getUsersName(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const [
    { data: areas, isLoading: areaLoading },
    { data: processes, isLoading: processLoading },
    { data: assets, isLoading: assetLoading },
  ] = useQueries([
    {
      queryKey: ['getAreas', selectedTenant.slug],
      queryFn: async () => {
        if (!selectedTenant.slug) return [];
        const result = await getAreasShort(selectedTenant.slug);
        return result?.data.length > 0 ? result?.data : [];
      },
    },
    {
      queryKey: ['getProcesses', selectedTenant.slug],
      queryFn: async () => {
        if (!selectedTenant.slug) return [];
        const result = await getProcessesShort(selectedTenant.slug);
        return result?.data.length > 0 ? result?.data : [];
      },
    },
    {
      queryKey: ['getAssets', selectedTenant.slug],
      queryFn: async () => {
        if (!selectedTenant.slug) return [];
        const result = await getAssetsShort(selectedTenant.slug);
        return result?.data.length > 0 ? result?.data : [];
      },
    },
  ]);

  const showForm = () => id ? !isLoading && !error : true;
  const title = id ? t('mapping:systems.form.editTitle') : t('mapping:systems.form.addTitle');

  return (
    <Container>
      {isLoading && <PageLoaderForm />}
      {!finished && showForm() && (
        <>
          <PageHeader
            title={title}
            segments={[
              { label: t('common:breadcrumbs.mapping') },
              { label: t('mapping:systems.list.title'), route: '/systems' },
              { label: title },
            ]}
            actions={
              <>
                <CancelBtn type="button" onClick={() => navigate('/systems')}>
                  {t('mapping:systems.form.cancel')}
                </CancelBtn>
                <SaveBtn
                  type="button"
                  disabled={isLoadingForm}
                  onClick={handleSubmit(handleComplete)}
                >
                  {t('mapping:systems.form.save')}
                </SaveBtn>
              </>
            }
          />

          <TabBar>
            <TabBtn active={activeTab === 0} onClick={() => setActiveTab(0)}>
              {t('mapping:systems.form.detailsStep')}
            </TabBtn>
            <TabBtn active={activeTab === 1} onClick={() => setActiveTab(1)}>
              {t('mapping:systems.form.bondsStep')}
            </TabBtn>
          </TabBar>

          {activeTab === 0 && (
            <Detalhes
              register={register}
              control={control}
              errors={errors}
              users={users}
              id={id}
              t={t}
            />
          )}
          {activeTab === 1 && (
            <Vinculos
              control={control}
              areas={areas}
              processes={processes}
              assets={assets}
              areaLoading={areaLoading}
              processLoading={processLoading}
              assetLoading={assetLoading}
              t={t}
            />
          )}
        </>
      )}
      {finished && (
        <FormSuccess
          title={t('mapping:systems.form.successMessage')}
          labelStartAgain={t('mapping:systems.form.addAnother')}
          handleStartAgain={() => handleReset()}
          labelShow={t('mapping:systems.form.viewDetails')}
          handleShow={() => navigate('/systems/show/' + idSystem)}
        />
      )}
    </Container>
  );
};
